import {
  Entity,
  Column,
  PrimaryGeneratedColumn,
  ManyToOne,
  OneToMany,
  OneToOne,
  JoinColumn,
  CreateDateColumn,
  UpdateDateColumn,
  PrimaryColumn,
} from 'typeorm';

import {
  ProposalStatus,
  ProposalAttachmentsStatus,
  ProposalItemStatus,
  ProposalCompanies,
  ProposalPaymentTerms,
  ProposalValidity,
  ProposalWarranty,
} from '../../interface/proposals/proposals-response.interface';

import { UsersEntity } from '../users/user.entity';
import { CompanyEntity } from '../companies/company.entity';
import { AttachmentsEntity } from '../attachments/attachments.entity';
import { Brand, CategoryTypes, ProductCategory } from '../../utils/enums.utils';
import { ProposalPricingSummaryEntity } from './proposal-pricing-summary.entity';


@Entity('proposals')
export class ProposalsEntity {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @Column()
  project_name!: string;

  @Column({ nullable: true })
  project_code?: string | null;

  @Column({ type: 'varchar' })
  company!: ProposalCompanies;

  @Column({ type: 'varchar' })
  category!: CategoryTypes;

  @Column({ type: 'varchar' })
  product_category!: ProductCategory;

  @ManyToOne(() => CompanyEntity, { nullable: false })
  @JoinColumn({ name: 'company_id' })
  company_ref!: CompanyEntity;

  @Column({ type: 'date' })
  selected_date!: Date;

  @Column({ nullable: true })
  customer_ref?: string | null;

  @Column({ nullable: true })
  quotation_number?: string | null;

  @Column({ type: 'varchar' })
  payment_terms!: ProposalPaymentTerms;

  @Column({ type: 'varchar' })
  quote_validity!: ProposalValidity;

  @Column({ type: 'varchar' })
  warranty!: ProposalWarranty;

  @Column({ nullable: true })
  company_remarks?: string | null;

  @Column({ type: 'varchar' })
  status!: ProposalStatus;

  @CreateDateColumn()
  created_at!: Date;

  @UpdateDateColumn()
  updated_at!: Date;

  // 🔹 Relations
  @OneToMany(() => ProposalAttachmentsEntity, attachment => attachment.proposal, { cascade: true })
  proposal_attachments?: ProposalAttachmentsEntity[];

  @OneToMany(() => ProposalItemsEntity, item => item.proposal, { cascade: true })
  proposal_items?: ProposalItemsEntity[];

  @OneToOne(() => ProposalPricingSummaryEntity, summary => summary.proposal, { cascade: true })
  proposal_pricing_summary?: ProposalPricingSummaryEntity;

  @ManyToOne(() => UsersEntity, { nullable: true })
  @JoinColumn({ name: 'created_by_id' })
  created_by?: UsersEntity | null;

  @ManyToOne(() => UsersEntity, { nullable: true })
  @JoinColumn({ name: 'updated_by_id' })
  updated_by?: UsersEntity | null;
}



@Entity('proposal_items')
export class ProposalItemsEntity {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @ManyToOne(() => ProposalsEntity, proposal => proposal.proposal_items, { nullable: false, onDelete: 'CASCADE' })
  @JoinColumn({ name: 'proposal_id' })
  proposal!: ProposalsEntity;

  @Column({ type: 'text', nullable: true })
  item_details?: string | null;

  @Column({ type: 'text', nullable: true })
  description?: string | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  quantity?: number | null;

  @Column({ nullable: true })
  uom?: string | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  buying_vat_inc?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  total_buying?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  shipping_fee?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  wsf?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  markup?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  sp?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  sp_each?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  misc?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  tpc_price?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  fsp?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  fsp_each?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  profit_each?: number | null;

  @Column({ type: 'decimal', precision: 15, scale: 2, nullable: true })
  total_profit?: number | null;

  @Column({ nullable: true })
  delivery?: string | null;

  @Column({ nullable: true })
  project?: string | null;

  @Column({ type: 'varchar' })
  brand!: Brand;

  @ManyToOne(() => AttachmentsEntity, { nullable: true })
  @JoinColumn({ name: 'attachment_item_img' })
  attachment_item_img?: AttachmentsEntity | null;

  @Column({ type: 'varchar' })
  status!: ProposalItemStatus;

  @Column({ nullable: true })
  lof?: string | null;

  @Column({ nullable: true })
  aof?: string | null;

  @Column({ nullable: true })
  srf?: string | null;

  @Column({ nullable: true })
  lsf?: string | null;
}

@Entity('proposal_attachments')
export class ProposalAttachmentsEntity {
  // Foreign key column for ProposalsEntity
  @PrimaryColumn('uuid', { name: 'proposal_id' })
  proposal_id!: string;

  @ManyToOne(() => ProposalsEntity, proposal => proposal.proposal_attachments, { nullable: false, onDelete: 'CASCADE' })
  @JoinColumn({ name: 'proposal_id' })
  proposal!: ProposalsEntity;

  // Foreign key column for AttachmentsEntity
  @PrimaryColumn('uuid', { name: 'attachment_id' })
  attachment_id!: string;

  @ManyToOne(() => AttachmentsEntity, { nullable: false })
  @JoinColumn({ name: 'attachment_id' })
  attachment!: AttachmentsEntity;

  @Column({ type: 'varchar' })
  status!: ProposalAttachmentsStatus;
}